import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LockKeyhole } from "lucide-react";
import { Alert, AlertDescription, Button, Field, Input } from "@pracownik/ui";
import { resetPasswordSchema, type ResetPasswordInput } from "@pracownik/shared/schemas";
import { authClient } from "@/lib/auth-client";
import { AuthLayout } from "@/layouts/auth-layout";

export function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [loading, setLoading] = useState(false);

  const form = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  const onSubmit = async (data: ResetPasswordInput) => {
    setLoading(true);
    try {
      const result = await authClient.resetPassword({ newPassword: data.newPassword, token });
      if (result.error) {
        toast.error(t("passwordResetFailed"));
        return;
      }
      toast.success(t("passwordResetSuccess"));
      navigate("/login");
    } catch {
      toast.error(t("passwordResetFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t("resetPassword")}
      description={t("resetPasswordDescription")}
      icon={<LockKeyhole />}
    >
      {!token && (
        <Alert variant="warning" className="mb-4">
          <AlertDescription>{t("resetTokenMissing")}</AlertDescription>
        </Alert>
      )}
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Field
          htmlFor="newPassword"
          label={t("newPassword")}
          hint={t("passwordPlaceholder")}
          error={form.formState.errors.newPassword && t("passwordTooShort")}
        >
          <Input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            autoFocus
            {...form.register("newPassword")}
          />
        </Field>
        <Field
          htmlFor="confirmPassword"
          label={t("confirmPassword")}
          error={form.formState.errors.confirmPassword && t("passwordsDontMatch")}
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            {...form.register("confirmPassword")}
          />
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={loading} disabled={!token}>
          {t("resetPassword")}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-medium text-primary hover:underline">
          {t("backToLogin")}
        </Link>
      </p>
    </AuthLayout>
  );
}
